import { FC, useId } from "react"

import { Avatar, Chip, Stack, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"

import { IStoreChargeStudent } from "../SendStoreChargeDialog.types"
import {
  BalanceCheck,
  formatStudentName,
  getExcludedPreview,
} from "../SendStoreChargeDialog.utils"

interface IProps {
  studentCount: number
  isRosterFallback: boolean
  balanceCheck: BalanceCheck | null
  excludedStudentIds: number[]
  excludedStudents: IStoreChargeStudent[]
}

const ReviewStudents: FC<IProps> = ({
  studentCount,
  isRosterFallback,
  balanceCheck,
  excludedStudentIds,
  excludedStudents,
}) => {
  const { t } = useTranslation()
  const chargingHeadingId = useId()
  const excludedPreview = getExcludedPreview(
    excludedStudents,
    excludedStudentIds.length,
  )

  return (
    <Stack gap={0.5}>
      <Typography
        id={chargingHeadingId}
        variant="body1"
        fontWeight={700}
        color="mockup.neutral10"
      >
        {isRosterFallback
          ? t("storeCharge.review.wholeClassFallback", {
              count: studentCount,
            })
          : t("storeCharge.review.chargingHeading", { count: studentCount })}
      </Typography>
      {balanceCheck && (
        <Stack
          component="ul"
          aria-labelledby={chargingHeadingId}
          direction="row"
          flexWrap="wrap"
          gap={1}
          sx={{ m: 0, p: 0, listStyle: "none" }}
        >
          {balanceCheck.rows.map(({ student }) => (
            <Chip
              key={student.id}
              component="li"
              avatar={<Avatar src={student.avatar_url} alt="" />}
              label={formatStudentName(student)}
              sx={(theme) => ({
                ...theme.typography.body1,
                height: "36px",
                borderRadius: "18px",
                bgcolor: "mockup.primary95",
                color: "mockup.neutral10",
                "& .MuiChip-avatar": {
                  width: "28px",
                  height: "28px",
                  color: "common.white",
                },
              })}
            />
          ))}
        </Stack>
      )}
      {isRosterFallback && (
        <>
          {excludedStudentIds.length > 0 && (
            <Typography variant="body3" color="mockup.neutral30">
              {t("storeCharge.review.excludedCount", {
                count: excludedStudentIds.length,
              })}
            </Typography>
          )}
          <Typography variant="body3" color="mockup.neutral20">
            {t("storeCharge.review.wholeClassFallbackNote")}
          </Typography>
        </>
      )}
      {!isRosterFallback && excludedStudentIds.length > 0 && (
        <Typography variant="body3" color="mockup.neutral30">
          {t("storeCharge.review.excluded", {
            names: [
              ...excludedPreview.names,
              ...(excludedPreview.moreCount > 0
                ? [
                    t("storeCharge.review.studentsOverflow", {
                      count: excludedPreview.moreCount,
                    }),
                  ]
                : []),
            ].join(", "),
          })}
        </Typography>
      )}
    </Stack>
  )
}

export default ReviewStudents
